import type { TimerListOutput } from "@orsane/schema/timers";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTRPC } from "@/lib/trpc";

type TimerField = { taskId: number; customAttributeId: number };

/**
 * Every timer mutation returns the whole refreshed entry list, so the panel can replace
 * its cache in one shot without reasoning about which sibling entries were auto-paused.
 * Starting a timer can also pause one on a different task, which is why `timer.active`
 * and the task queries are invalidated rather than patched.
 */
function useTimerMutationSideEffects(field: TimerField) {
    const trpc = useTRPC();
    const queryClient = useQueryClient();

    return (data: TimerListOutput) => {
        queryClient.setQueryData(trpc.timer.list.queryKey(field), data);
        queryClient.invalidateQueries({ queryKey: trpc.timer.list.queryKey() });
        queryClient.invalidateQueries({ queryKey: trpc.timer.active.queryKey() });
        queryClient.invalidateQueries({ queryKey: trpc.task.get.queryKey({ id: field.taskId }) });
        queryClient.invalidateQueries({ queryKey: trpc.taskUpdates.list.queryKey({ taskId: field.taskId }) });
    };
}

export function useStartTimerMutation(field: TimerField) {
    const trpc = useTRPC();
    const onChanged = useTimerMutationSideEffects(field);
    return useMutation(trpc.timer.start.mutationOptions({ onSuccess: onChanged }));
}

export function useAddTimerEntryMutation(field: TimerField) {
    const trpc = useTRPC();
    const onChanged = useTimerMutationSideEffects(field);
    return useMutation(trpc.timer.add.mutationOptions({ onSuccess: onChanged }));
}

export function useResumeTimerMutation(field: TimerField) {
    const trpc = useTRPC();
    const onChanged = useTimerMutationSideEffects(field);
    return useMutation(trpc.timer.resume.mutationOptions({ onSuccess: onChanged }));
}

export function usePauseTimerMutation(field: TimerField) {
    const trpc = useTRPC();
    const onChanged = useTimerMutationSideEffects(field);
    return useMutation(trpc.timer.pause.mutationOptions({ onSuccess: onChanged }));
}

export function useUpdateTimerDurationMutation(field: TimerField) {
    const trpc = useTRPC();
    const onChanged = useTimerMutationSideEffects(field);
    return useMutation(trpc.timer.updateDuration.mutationOptions({ onSuccess: onChanged }));
}

export function useDeleteTimerEntryMutation(field: TimerField) {
    const trpc = useTRPC();
    const onChanged = useTimerMutationSideEffects(field);
    return useMutation(trpc.timer.delete.mutationOptions({ onSuccess: onChanged }));
}
